/**
 * AnimatedStat — smooth count-up number for live archive statistics.
 * - skeleton placeholder while value is null (never flashes wrong numbers)
 * - animates 0 → value on first render (~1s), previous → new on change
 * - respects prefers-reduced-motion
 * - subtle glow pulse when counting completes
 */
import React, { useEffect, useRef, useState } from "react";

const reduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const AnimatedStat = ({ value, suffix = "", className = "", testid }) => {
  const [display, setDisplay] = useState(null);
  const [pulse, setPulse] = useState(false);
  const fromRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (value === null || value === undefined) return;
    if (reduced()) { setDisplay(value); fromRef.current = value; return; }
    const from = fromRef.current;
    if (from === value) { setDisplay(value); return; }
    const start = performance.now();
    const dur = 1000;
    const step = (now) => {
      const t = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (value - from) * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = value;
        setPulse(true);
        setTimeout(() => setPulse(false), 900);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);

  if (value === null || value === undefined || display === null) {
    return (
      <span className={`inline-block animate-pulse rounded-sm ${className}`}
            style={{ background: "#2D2A26", width: "3ch", height: "1em" }}
            data-testid={testid ? `${testid}-skeleton` : undefined} />
    );
  }
  return (
    <span className={className} data-testid={testid}
          style={{ transition: "text-shadow 0.6s ease",
                   textShadow: pulse ? "0 0 14px rgba(217,45,32,0.55)" : "none" }}>
      {display.toLocaleString()}{suffix}
    </span>
  );
};
